/**
 * Список режимов, доступных пользователю.
 *
 * Один и тот же список нужен в двух местах: на экране результата — «чем
 * заняться дальше» — и на главной — «чем заняться вообще». Две копии
 * разошлись бы при первом же новом режиме, и пользователь увидел бы разный
 * набор в зависимости от того, откуда пришёл.
 */

import type { LanguageCode } from '@type2x/shared-contracts';
import type { Translate } from '@type2x/i18n';
import { Button, KeyCap } from '@type2x/ui';
import type { SymbolMode } from '@type2x/feature-symbols';
import type { PackMode } from '@type2x/feature-packs';
import { TEMPO_MODES, type TempoMode } from '@type2x/feature-tempo';

import type { ScriptReadiness } from '../state/script-readiness.js';
import { ModeCard } from './ModeCard.js';

/**
 * Идентификатор объяснения к закрытым режимам.
 *
 * Кнопка ссылается на него `aria-describedby`: пользователь экранного диктора
 * иначе услышал бы только «недоступно», без причины и без того, что её снимет.
 */
const LOCKED_HINT_ID = 'locked-modes-hint';

export interface ModeMenuProps {
  readonly t: Translate;
  readonly onStartMistakes: () => void;
  readonly onStartSentences: () => void;
  readonly onStartFundamentals: () => void;
  readonly onStartSymbols: (mode: SymbolMode) => void;
  readonly onStartPacks: (mode: PackMode) => void;
  readonly onStartLayoutSwitch: () => void;
  readonly onStartTempo: (mode: TempoMode) => void;
  readonly onStartCustomText: () => void;
  /** Режимы символов, которым ещё есть чему учить: пройденный не предлагается. */
  readonly symbolModes: readonly SymbolMode[];
  /** Режимы пакетов, которым ещё есть чему учить: пройденный не предлагается. */
  readonly packModes: readonly PackMode[];
  /** Остались ли непройденные группы клавиш: без них «Основы» показывать нечего. */
  readonly fundamentalsAvailable: boolean;
  /** Есть ли слабые места: без них режим «Мои ошибки» показывать нечего. */
  readonly mistakesAvailable: boolean;
  /** Освоена ли латиница: без неё пакеты кода и адресов только объясняются. */
  readonly latin: ScriptReadiness;
  /** Освоены ли обе раскладки режима переключения. */
  readonly layoutSwitch: ScriptReadiness;
  /** Смена языка тренировки: ею заканчивается объяснение закрытых режимов. */
  readonly onTrainingLanguageChange: (language: LanguageCode) => void;
}

/**
 * Раздел меню: заголовок, значок-клавиша и кнопки режимов.
 *
 * Пустой раздел не показывается: заголовок без единой кнопки под ним
 * сообщает, что где-то что-то потерялось.
 */
function ModeGroup({
  title,
  glyph,
  children,
}: {
  readonly title: string;
  readonly glyph: string;
  readonly children: React.ReactNode;
}): React.JSX.Element | null {
  const items = Array.isArray(children) ? children.flat() : [children];
  if (items.every((item) => item === false || item === null || item === undefined)) return null;

  return (
    <section className="t2x-modes__group">
      <h3 className="t2x-modes__title">
        <KeyCap glyph={glyph} />
        {title}
      </h3>
      <div className="t2x-modes__items">{children}</div>
    </section>
  );
}

/**
 * Кнопки режимов, разобранные по разделам.
 *
 * Шестнадцать кнопок подряд читаются стеной: продолжение занятия стояло рядом
 * с пунктуацией и с сохранением прогресса — вещи разного рода вперемешку.
 * Разделы отвечают на вопрос «чем именно заняться» (ТЗ FR-EN-82).
 *
 * Объяснение закрытых режимов по-прежнему стоит отдельным блоком.
 */
export function ModeMenu(props: ModeMenuProps): React.JSX.Element {
  const { t, symbolModes, fundamentalsAvailable, mistakesAvailable } = props;

  return (
    <div className="t2x-modes">
      <ModeGroup title={t('modes.group.learn')} glyph="а">
        {fundamentalsAvailable && (
          <ModeCard
            t={t}
            title={t('fundamentals.title')}
            purpose={t('fundamentals.purpose')}
            onOpen={props.onStartFundamentals}
          />
        )}
        {mistakesAvailable && (
          <ModeCard
            t={t}
            title={t('mistakes.title')}
            purpose={t('mistakes.purpose')}
            onOpen={props.onStartMistakes}
          />
        )}
        <ModeCard
          t={t}
          title={t('sentences.title')}
          purpose={t('sentences.purpose')}
          onOpen={props.onStartSentences}
        />
      </ModeGroup>

      <ModeGroup title={t('modes.group.signs')} glyph="?">
        {symbolModes.map((mode) => (
          <ModeCard
            key={mode}
            t={t}
            title={t(`symbols.title.${mode}`)}
            purpose={t(`symbols.purpose.${mode}`)}
            onOpen={() => props.onStartSymbols(mode)}
          />
        ))}
      </ModeGroup>

      <ModeGroup title={t('modes.group.latin')} glyph="A">
        <ScriptModes {...props} />
      </ModeGroup>

      <ModeGroup title={t('modes.group.own')} glyph="»">
        {TEMPO_MODES.map((mode) => (
          <ModeCard
            key={mode}
            t={t}
            title={t(`tempo.title.${mode}`)}
            purpose={t(`tempo.purpose.${mode}`)}
            onOpen={() => props.onStartTempo(mode)}
          />
        ))}
        <ModeCard
          t={t}
          title={t('customText.title')}
          purpose={t('customText.purpose')}
          onOpen={props.onStartCustomText}
        />
      </ModeGroup>
    </div>
  );
}

/**
 * Режимы, привязанные к раскладке: пакеты кода и адресов и переключение.
 *
 * Они стоят вместе, потому что закрываются по одной причине — незнакомой
 * раскладке, — но условия у них разные: пакетам нужна латиница, переключению —
 * обе раскладки его упражнения (`state/script-readiness.ts`).
 *
 * Закрытая кнопка остаётся живой: нажатие ведёт к объяснению, а не в никуда.
 * Кнопка, которая не отвечает, читается как сломанная — так о ней и сообщили
 * при первом же живом использовании.
 */
function ScriptModes({
  t,
  latin,
  layoutSwitch,
  packModes,
  onStartPacks,
  onStartLayoutSwitch,
}: ModeMenuProps): React.JSX.Element {
  const closed = { hintId: LOCKED_HINT_ID };

  return (
    <>
      {packModes.map((mode) => (
        <ModeCard
          key={mode}
          t={t}
          title={t(`packs.title.${mode}`)}
          purpose={t(`packs.purpose.${mode}`)}
          locked={latin.ready ? undefined : closed}
          onOpen={() => onStartPacks(mode)}
        />
      ))}
      <ModeCard
        t={t}
        title={t('layoutSwitch.title')}
        purpose={t('layoutSwitch.purpose')}
        locked={layoutSwitch.ready ? undefined : closed}
        onOpen={onStartLayoutSwitch}
      />
    </>
  );
}

/**
 * Объяснение к закрытым режимам и переход к тому, что их откроет.
 *
 * Закрытый режим не исчезает, а объясняется: исчезнувший режим читается как
 * «такого не бывает». Сказать же, что его откроет, и не дать этого сделать —
 * половина ответа, поэтому рядом стоит переход к нужному языку.
 *
 * Объяснение одно, и выбирается оно по недостающей раскладке: своя раскладка
 * засчитана с самого начала, поэтому недостающая всегда одна, а набор закрытых
 * ею режимов по ней однозначен.
 */
export function LockedHint({
  t,
  latin,
  layoutSwitch,
  onTrainingLanguageChange,
}: Pick<
  ModeMenuProps,
  't' | 'latin' | 'layoutSwitch' | 'onTrainingLanguageChange'
>): React.JSX.Element | null {
  const blocking = latin.ready ? layoutSwitch : latin;
  if (blocking.ready) return null;

  return (
    <div className="t2x-locked">
      <p className="t2x-muted" id={LOCKED_HINT_ID}>
        {t(`modes.locked.${blocking.language}`, {
          mastered: blocking.mastered,
          total: blocking.total,
        })}
      </p>
      <Button variant="secondary" onClick={() => onTrainingLanguageChange(blocking.language)}>
        {t(`modes.train.${blocking.language}`)}
      </Button>
    </div>
  );
}
